
/**
 * `VerticalNavigation` - Component used to to help the user navigate through different sections of the PI page.
 * At the moment this component is only being displayed on MQ5 or larger screens.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { VerticalNavigationComponent } from './VerticalNavigation';
import {
  ANCHOR_ATTR_NAME,
  COMPONENT_NAME,
  INITIAL_SHOW_TIME,
  SECTION_SHOW_TIME,
} from './VerticalNavigation.config';

export default (ComponentInitializeDispatcher as VerticalNavigationComponent).extend({
  name: COMPONENT_NAME,
  data: () => ({
    activeItemIndex: -1,
    animationClass: '',
    sectionClass: '',
    timeout: undefined,
    observer: undefined,
    sectionsDataAnchorIds: [],
    sections: [],
  }),
  mounted() {
    this.setupIntersectionObserver();
    this.observeSections(`[${ANCHOR_ATTR_NAME}]`);
    this.listenForNewSections();
    this.activeItemIndex = 0;
    this.animationClass = 'owc-vertical-navigation__list--open';
    window.setTimeout(() => {
      this.animationClass = '';
    }, INITIAL_SHOW_TIME);
  },
  beforeDestroy() {
    this.handleDestroy();
  },
  computed: {
    anchorItems: function() {
      return this.componentData?.anchorItems;
    },
    scrollHint: function() {
      return this.componentData?.scrollHint;
    },
    isScrollHintVisible: function() {
      return this.activeItemIndex <= 0;
    },
  },
  methods: {
    setupIntersectionObserver: function(): void {
      const options = {
        rootMargin: '0px',
        threshold: 0.5,
      };
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute(ANCHOR_ATTR_NAME);
            const currentIndex = (this.anchorItems || []).findIndex(anchorItem => anchorItem.id === id);
            if (currentIndex > -1) {
              this.activeItemIndex = currentIndex;
            }
          }
        });
      }, options);
    },
    observeSections: function(query): void {
      const parentElement = (this.$el.getRootNode() as ShadowRoot).host?.parentElement;

      if (!parentElement) {
        return;
      }

      const sections = [...parentElement.querySelectorAll(query)];

      if (!sections.length) {
        return;
      }

      if (!this.areNewSections(sections)) {
        return;
      }

      this.sections.forEach(section => this.observer?.observe(section));
    },
    eventListener: function(event: CustomEvent<{ section: string }>) {
      this.observeSections(`[${ANCHOR_ATTR_NAME}="${event.detail.section}"]`);
    },
    listenForNewSections: function() {
      window.addEventListener('owc-vertical-navigation-add-section', this.eventListener as EventListener);
    },
    areNewSections: function(sections) {
      const newSectionsDataAnchorIds = sections.map(
        section => section.getAttribute('data-anchor-id') as string,
      );
      const combinedSectionsDataAnchorIds = [
        ...new Set([...this.sectionsDataAnchorIds, ...newSectionsDataAnchorIds]),
      ];
      if (combinedSectionsDataAnchorIds.length === this.sectionsDataAnchorIds.length) {
        return false;
      }
      this.sectionsDataAnchorIds = combinedSectionsDataAnchorIds;
      this.sections = [...new Set([...this.sections, ...sections])];
      return true;
    },
    handleDestroy: function() {
      window.removeEventListener('owc-vertical-navigation-add-section', this.eventListener as EventListener);
      this.sections.forEach((section: Element) => {
        this.observer?.unobserve(section);
      });
      this.observer?.disconnect();
    },
  },
  watch: {
    activeItemIndex: function() {
      this.sectionClass = 'owc-vertical-navigation__show';
      clearTimeout(this.timeout);
      this.timeout = window.setTimeout(() => {
        this.sectionClass = '';
      }, SECTION_SHOW_TIME);
    },
  },
});
